<template>
  <div>
    <v-row>
      <v-col xs="12" sm="6" md="4">
        <v-list>
          <v-list-item-group
            v-model="activeIndex"
            active-class="border"
            color="primary"
          >
            <v-list-item
              @click="setActiveRole(index)"
              v-for="(role, index) in roles"
              :key="role._id"
            >
              <v-list-item-content>
                <v-list-item-title v-text="role.name"></v-list-item-title>
                <v-list-item-subtitle
                  v-text="role.description"
                ></v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-icon>
                <v-icon v-if="activeIndex === index"
                  >keyboard_arrow_right</v-icon
                >
              </v-list-item-icon>
            </v-list-item>
          </v-list-item-group>
        </v-list>
        <v-col v-if="canAddRole" xs="12">
          <v-flex style="float: right">
            <v-btn color="primary" small dark @click="addRoleModal = true">
              Add Role
              <v-icon>add</v-icon>
            </v-btn>
          </v-flex>
        </v-col>
      </v-col>
      <v-col xs="12" sm="6" md="7">
        <v-card>
          <v-card-title>Role Details:</v-card-title>
          <div class="pb-0 pr-6">
            <v-col>
              <v-text-field
                v-model="activeRole.name"
                outlined
                label="Role name"
                dense
              />
            </v-col>
            <v-col>
              <v-text-field
                class="mb-0"
                v-model="activeRole.description"
                outlined
                label="Role description"
                dense
              />
            </v-col>
            <v-card-title class="mt-0 pt-0">Assigned Permissions:</v-card-title>
            <v-divider />
            <div class="permissions-container">
              <permissions-container
                :key="permissionsContainerKey"
                :editable="canUpdateRole"
                :permissions="activeRole.permissions"
                @onChangePermission="updatePermissionDependencies"
              />
            </div>
          </div>
          <v-card-actions>
            <v-spacer />
            <v-btn v-if="canUpdateRole" @click="saveRole" color="primary"
              >Save</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <add-role-modal
      v-if="addRoleModal"
      :addRoleModal.sync="addRoleModal"
      :permissions="permissions"
      @save="saveNewRole"
      title="Add Role"
    />
  </div>
</template>

<script>
import request from "@/modules/request";
import AddRoleModal from "@/components/shared/modals/AddRoleModal";
import PermissionsContainer from "@/components/shared/PermissionsContainer";
import { hasPermission } from "@/modules/rbac/permission-manifest";

export default {
  name: "Roles",
  inject: ["snackBar", "reactiveInjected"],
  components: {
    AddRoleModal,
    PermissionsContainer,
  },
  async mounted() {
    await this.loadData();
    this.setActiveRole(0);
  },
  data() {
    return {
      permissionsContainerKey: 0,
      loading: true,
      roles: [],
      permissions: [],
      activeIndex: 0,
      addRoleModal: false,
      selectedPermissions: [],
      activeRole: {},
      canUpdateRole: hasPermission(
        "canUpdateRole",
        null,
        this.reactiveInjected.activeUser
      ),
      canAddRole: hasPermission(
        "canCreateRole",
        null,
        this.reactiveInjected.activeUser
      ),
    };
  },
  methods: {
    updatePermissionDependencies(updatedPermissionIndex) {
      const updatedPermission = this.activeRole.permissions[
        updatedPermissionIndex
      ];
      const { dependencies = [], selected, shortName } = updatedPermission;

      dependencies.forEach((dependency) => {
        const { shortName: dependencyShortName, inverse } = dependency;
        this.activeRole.permissions.forEach((permission, permissionIndex) => {
          if (permission.shortName === dependencyShortName) {
            if (selected && dependency.value === true) {
              // if selected is true and requires dependency value to also be true
              this.activeRole.permissions[permissionIndex].selected = true;
            } else if (selected && dependency.value === false) {
              // if selected is true and requires dependency value to be false
              this.activeRole.permissions[permissionIndex].selected = false;
            }
          }
        });
      });

      this.activeRole.permissions.forEach((permission, index) => {
        const { dependencies = [] } = permission;
        dependencies.find((dependency, dependencyIndex) => {
          if (dependency.shortName === shortName) {
            if (!selected && dependency.value === true) {
              this.activeRole.permissions[index].selected = false;
            }
          }
        });
      });

      this.permissionsContainerKey += 1;
    },
    setActiveRole(index) {
      const { roles, permissions } = this;
      this.activeRole = JSON.parse(JSON.stringify(roles[index]));
      this.activeRole.permissions = permissions.map((permission) => {
        permission.selected =
          roles[index].permissions.findIndex(
            (rolePermission) => rolePermission._id === permission._id
          ) > -1;
        return permission;
      });
    },
    async loadData() {
      const { getRoles, getPermissions } = this;
      const results = await Promise.all([
        getRoles("associate"),
        getPermissions(),
      ])
        .then((results) => {
          return results;
        })
        .catch((error) => {
          console.error(error);
        });

      const [roles, permissions] = results;
      Object.assign(this, {
        roles,
        permissions,
      });
      this.loading = false;
    },
    async getRoles(roleType) {
      return request
        .callInternal({
          method: "get",
          route: `/api/admin/roles?roleType=${roleType}`,
        })
        .then((results) => {
          return results.data;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async getPermissions() {
      return request
        .callInternal({
          method: "get",
          route: `/api/admin/permissions`,
        })
        .then((results) => {
          return results.data;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async saveNewRole(ctx) {
      return request
        .callInternal({
          method: "post",
          route: "/api/admin/roles",
          data: ctx,
        })
        .then(() => {
          this.snackBar("New role added!", "success");
          this.addRoleModal = false;
          this.loadData();
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async saveRole() {
      const { activeRole, activeIndex } = this;
      activeRole.permissions = activeRole.permissions
        .filter((perm) => perm.selected)
        .map((perm) => perm._id);

      return request
        .callInternal({
          method: "patch",
          route: `/api/admin/roles/${activeRole._id}`,
          data: activeRole,
        })
        .then(async () => {
          this.snackBar("Role saved!", "success");
          await this.loadData();
          this.setActiveRole(activeIndex);
        })
        .catch((error) => {
          console.error(error);
        });
    },
  },
};
</script>

<style scoped>
.permissions-container {
  height: 45vh;
  overflow-y: auto;
}
</style>
